(() => {
  // Guard against the IIFE running more than once (e.g. if the script tag is
  // present in markup that gets re-injected). The document-level listeners
  // registered below must only be bound a single time.
  if (window.__giftRecipientFieldsInit) return;
  window.__giftRecipientFieldsInit = true;

  // Characters rejected outright in every gift field. None has a legitimate use
  // in a person's name, a dog's name or a gift message, and each is an
  // injection vector somewhere downstream of the cart:
  //   < >   open HTML tags (the fields are echoed into the cart, the account
  //         order page, packing slips and the gift email)
  //   { }   open Liquid and JS template expressions — {{ }}, {% %}, ${ } — which
  //         matters because the gift message is rendered inside an email template
  //   \ `   break JSON string escaping and JS template literals
  //   |     separates shell commands and CSV/TSV columns in order exports
  //   ;     terminates a SQL statement
  //
  // Catching these here is not just hardening. Shopify's edge WAF answers any
  // /cart/add.js POST carrying a markup-shaped payload with an HTML 430
  // "Security Rejection" page instead of JSON, so a value like <script> makes
  // the theme's cart handler die on JSON.parse and add-to-cart fail with
  // nothing the shopper can act on.
  //
  // Apostrophes and double quotes are deliberately allowed: they are the most
  // common special characters in real names (O'Brien, D'Angelo) and carry no
  // SQL risk against parameterized queries. So are accents, emoji and other
  // non-ASCII text — the fields have to accept names like Renée and 田中.
  const BLOCKED_CHARACTERS = /[<>{}\\|;`]/g;

  // Characters removed silently rather than flagged, because the shopper cannot
  // see them and so cannot act on an error about them: C0/C1 control codes
  // (Rack rejects a null byte in params outright), zero-width characters (which
  // would otherwise satisfy the required-field check while looking blank) and
  // bidi overrides (which reverse surrounding text in the cart, at checkout and
  // on packing slips). Tab, newline and carriage return are preserved for the
  // gift message textarea.
  const INVISIBLE_CHARACTERS =
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

  // References to the current DOM nodes. These are refreshed by init() whenever
  // the product-info section is re-rendered (e.g. commitment/variation swap).
  let recipientNameInput;
  let emailInput;
  let messageInput;
  let charCount;
  // One descriptor per field, pairing the visible input with its error element
  // and the hidden input that carries its value as a line item property. Built
  // by init() so validation, persistence and event binding can all iterate the
  // same list instead of repeating themselves five times.
  let fields = [];
  // The product form the hidden property inputs are associated with, so an
  // add-to-cart from another form on the page (a quick-buy card, a recommended
  // product) isn't mistaken for this gift subscription being added.
  let giftForm = null;
  let maxLength = 500;
  // A required field's only invalid state may be "empty", and surfacing that
  // immediately would flag a field the shopper has not reached yet. So the
  // empty-field error waits until the field has been visited and left blank, or
  // until add-to-cart is attempted. Holds the current render's input nodes, and
  // is replaced wholesale by init() because a re-render makes them all new.
  let touchedRequiredInputs = new Set();

  // Entered values keyed by input id. Toggling between variation products
  // replaces the whole product-info section (see bindContractButtons in
  // custom.js), which wipes these inputs, and the swap also pushes a new URL —
  // so a back/forward navigation between variations is a full page load.
  // sessionStorage survives both, and is scoped to the tab so a later visit
  // starts from empty fields.
  const STORAGE_KEY = 'barkGiftRecipientFields';

  function readSavedValues() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      // Private browsing or malformed data — persistence is best effort.
      return {};
    }
  }

  let savedValues = readSavedValues();

  function saveValue(input) {
    savedValues[input.id] = input.value;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedValues));
    } catch (error) {
      // Quota or private browsing — keep the in-memory copy, which still covers
      // the AJAX section swap.
    }
  }

  function clearSavedValues() {
    savedValues = {};
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Private browsing — the in-memory copy is already cleared.
    }
  }

  function stripInvisible(value) {
    return value.replace(INVISIBLE_CHARACTERS, '');
  }

  // Returns the blocked characters present in a value, de-duplicated and in the
  // order they appear, so the error can name each one once ('< >' rather than
  // '< < >'). Empty string when the value is clean.
  function findBlockedCharacters(value) {
    const matches = value.match(BLOCKED_CHARACTERS);
    if (!matches) return '';
    return Array.from(new Set(matches)).join(' ');
  }

  // Drop invisible characters as they arrive — most often pasted in from a word
  // processor or a messaging app rather than typed.
  function stripInvisibleInPlace(input) {
    const sanitized = stripInvisible(input.value);
    if (sanitized === input.value) return;

    // Assigning value moves the caret to the end of the field, so put it back,
    // shifted by however many characters were removed ahead of it.
    const caret = input.selectionStart;
    const before = input.value.slice(0, caret);
    const newCaret = caret - (before.length - stripInvisible(before).length);

    input.value = sanitized;

    try {
      input.setSelectionRange(newCaret, newCaret);
    } catch (error) {
      // The selection APIs throw on input type="email"; the value is still
      // sanitized, only the caret position is lost.
    }
  }

  function restoreValue({ input }) {
    const saved = savedValues[input.id];
    if (!saved) return;

    // A tampered or stale sessionStorage entry is untrusted input: assigning
    // value programmatically bypasses the maxlength attribute, so re-apply the
    // limit here as well as sanitizing.
    const limit = parseInt(input.getAttribute('maxlength'), 10);
    let value = stripInvisible(saved);
    if (limit > 0) value = value.slice(0, limit);

    input.value = value;
    // The property is left alone — updateFieldState() syncs every field from
    // its restored value once all of them are back in place.
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function isFilled(input) {
    return input.value.trim().length > 0;
  }

  // Both required properties must be present or Rails drops the gift order, and
  // a blocked character anywhere would get the whole add-to-cart POST rejected
  // at Shopify's edge, so the add is allowed only when every field is clean.
  function canAddToCart() {
    const allFieldsClean = fields.every(({ input }) => findBlockedCharacters(input.value) === '');
    return allFieldsClean && isValidEmail(emailInput.value) && isFilled(recipientNameInput);
  }

  function setFieldError(input, errorElement, message) {
    const showError = message.length > 0;

    input.setAttribute('aria-invalid', showError ? 'true' : 'false');

    if (!errorElement) return;
    errorElement.hidden = !showError;
    errorElement.textContent = message;
  }

  // Mirror a visible field's value into its hidden line item property input.
  // Disabled inputs are excluded from FormData, so we only enable (i.e. submit)
  // a property when it has a value — keeping empty optional fields out of the
  // line item properties entirely.
  function syncProperty(propertyInput, value) {
    if (!propertyInput) return;
    const trimmed = value.trim();
    propertyInput.value = trimmed;
    propertyInput.disabled = trimmed.length === 0;
  }

  function updateCharCount() {
    if (!messageInput || !charCount) return;
    charCount.textContent = `${messageInput.value.length} / ${maxLength}`;
  }

  // The error message for a field, or empty string when it is valid. Blocked
  // characters are checked first because that failure applies to every field.
  // An empty required field only reports once it has been touched — see
  // touchedRequiredInputs.
  function getErrorMessage(input) {
    const value = input.value.trim();
    const blocked = findBlockedCharacters(value);

    if (blocked) {
      return `Please remove these characters: ${blocked}`;
    }

    const isEmpty = value.length === 0;
    const reportEmpty = isEmpty && touchedRequiredInputs.has(input);

    if (input === emailInput) {
      if (reportEmpty) return "Please enter the recipient's email address.";
      if (!isEmpty && !isValidEmail(value)) return 'Please enter a valid email address.';
    }

    if (input === recipientNameInput && reportEmpty) {
      return "Please enter the recipient's name.";
    }

    return '';
  }

  // Error messaging and hidden-property sync for every field. Runs on every
  // keystroke, on blur, after values are restored, and on a blocked add.
  function updateFieldState() {
    fields.forEach(({ input, property, error }) => {
      const message = getErrorMessage(input);

      setFieldError(input, error, message);

      // A value that fails validation is never mirrored into its line item
      // property, so it cannot reach the cart even if the add is somehow not
      // blocked. Names otherwise pass through untouched apart from the trim.
      syncProperty(property, message.length > 0 ? '' : input.value);
    });
  }

  // Drop every reference to the previous render. init() runs again after each
  // product-info swap and the newly rendered section may have no gift fields at
  // all, in which case a null giftForm leaves every add-to-cart alone.
  function resetState() {
    recipientNameInput = null;
    emailInput = null;
    messageInput = null;
    charCount = null;
    fields = [];
    giftForm = null;
  }

  // The gift details are in the cart line's properties once the add succeeds,
  // so drop the session copy rather than leave a recipient's name and email
  // sitting in the tab's storage for the rest of the visit. The visible fields
  // are emptied along with it — otherwise the form would keep showing details
  // that are no longer persisted, and a variation swap would silently blank
  // them.
  function onCartAdd(event) {
    if (!emailInput) return;
    // variant:add bubbles from whichever form was submitted. Note that an add
    // from anywhere else on the page — a recommended product, or the Rebuy
    // Smart Cart's in-cart upsells, which dispatch rebuy:cart.add and never
    // this event — is simply none of this script's business.
    if (giftForm && event.target !== giftForm) return;

    clearSavedValues();
    fields.forEach(({ input }) => {
      input.value = '';
    });
    touchedRequiredInputs = new Set();

    updateCharCount();
    updateFieldState();
  }

  // Surface why the add was refused and send the shopper to the first field
  // that needs attention. Marking the required fields touched is what lets an
  // untouched empty one report itself.
  function reportInvalidAdd() {
    [recipientNameInput, emailInput].forEach((input) => touchedRequiredInputs.add(input));
    updateFieldState();

    const firstInvalid = fields.find(
      ({ input }) => input.getAttribute('aria-invalid') === 'true'
    );
    firstInvalid?.input.focus();
  }

  // Block an add-to-cart carrying missing or invalid gift details, rather than
  // holding the ATC button disabled until the fields are filled. Validating at
  // the moment of the click keeps this script from sharing the button's
  // disabled state with everything else that writes to it — BuyButtons
  // re-deriving it from variant availability, CustomButton clearing it on
  // rebuy:cart.add, the product-info section swap re-rendering it — each of
  // which would otherwise need its own re-assertion here.
  //
  // The theme adds its add-to-cart handler as a click listener on the button
  // itself (see ProductForm in theme.js.liquid), so stopping propagation during
  // the capture phase is what prevents the add; preventDefault alone would not.
  // preventDefault is still needed to suppress implicit form submission.
  function onAtcCapture(event) {
    if (!giftForm) return;

    const submitter = event.target.closest?.('[type="submit"]');
    if (!submitter || submitter.form !== giftForm) return;
    if (canAddToCart()) return;

    event.preventDefault();
    event.stopPropagation();
    reportInvalidAdd();
  }

  function init() {
    resetState();

    const wrapper = document.querySelector('[data-gift-recipient-fields]');
    if (!wrapper) return;

    recipientNameInput = wrapper.querySelector('#gift-recipient-name');
    emailInput = wrapper.querySelector('#gift-recipient-email');
    messageInput = wrapper.querySelector('#gift-message');
    charCount = wrapper.querySelector('#gift-message-char-count');

    fields = [
      ['#gift-from-name', '#gift-from-name-property', '#gift-from-name-error'],
      ['#gift-recipient-name', '#gift-recipient-name-property', '#gift-recipient-name-error'],
      ['#gift-recipient-email', '#gift-recipient-email-property', '#gift-recipient-email-error'],
      ['#gift-dog-name', '#gift-dog-name-property', '#gift-dog-name-error'],
      ['#gift-message', '#gift-message-property', '#gift-message-error'],
    ]
      .map(([inputSelector, propertySelector, errorSelector]) => ({
        input: wrapper.querySelector(inputSelector),
        property: wrapper.querySelector(propertySelector),
        error: wrapper.querySelector(errorSelector),
      }))
      .filter(({ input }) => input);

    if (!recipientNameInput || !emailInput) {
      resetState();
      return;
    }

    // Every hidden property carries the same form attribute, so any of them
    // resolves the product form these fields submit with.
    const boundProperty = fields.find(({ property }) => property);
    giftForm = boundProperty ? boundProperty.property.form : null;

    maxLength = messageInput ? parseInt(messageInput.getAttribute('maxlength'), 10) : 500;
    // The re-rendered fields are new, unvisited nodes.
    touchedRequiredInputs = new Set();

    // Refill the fresh nodes from anything already entered this session so a
    // variation swap doesn't make the shopper retype the gift details.
    fields.forEach(restoreValue);

    updateCharCount();
    updateFieldState();

    fields.forEach(({ input }) => {
      input.addEventListener('input', () => {
        stripInvisibleInPlace(input);
        if (input === messageInput) updateCharCount();
        saveValue(input);
        updateFieldState();
      });
    });

    [recipientNameInput, emailInput].forEach((input) => {
      input.addEventListener('blur', () => {
        touchedRequiredInputs.add(input);
        updateFieldState();
      });
    });
  }

  document.addEventListener('variant:add', onCartAdd);

  // Capture phase, so this runs before the theme's own click handler on the
  // add-to-cart button. See onAtcCapture.
  document.addEventListener('click', onAtcCapture, true);

  // Belt and braces for a submission that doesn't originate in a button click,
  // e.g. form.requestSubmit(). Implicit submission (Enter in a form field) is
  // already covered by the click handler's preventDefault.
  document.addEventListener(
    'submit',
    (event) => {
      if (!giftForm || event.target !== giftForm) return;
      if (canAddToCart()) return;

      event.preventDefault();
      event.stopPropagation();
      reportInvalidAdd();
    },
    true
  );

  // A commitment/variation-product swap replaces the whole product-info
  // section (including these fields and the buy buttons) via AJAX in custom.js,
  // which dispatches loadingStateChanged when done. Re-init against the fresh
  // DOM so the fields and the form reference point at the new nodes.
  document.addEventListener('loadingStateChanged', init);

  // The script tag is deferred, so the parser has finished and readiness is
  // already "interactive" by the time this runs — the whole product-info
  // section is in the DOM and there is nothing to wait for.
  init();
})();
